import type { PlanDefinition } from '@medplum/fhirtypes';

import { PLAN_SHAPE_EXTENSION_URL } from './constants';
import { continuousFhirToSpec, continuousSpecToResources } from './continuous';
import { episodicFhirToSpec, episodicSpecToResources } from './episodic';
import {
  isPhaseAction,
  phasedFhirToSpec,
  phasedSpecToResources,
} from './phased';
import type { CompiledPlan, DecodeContext } from './shared';
import type { PlanShapeCode, PlanSpec } from './spec';

export type { CompiledPlan, DecodeContext } from './shared';

// The Plan Shape Registry: each shape's module — label copy for the UI, the
// deterministic FHIR adapters. Render primitives live in the frontend feature
// lib, keyed by the same codes. Adding a shape = one entry + its module;
// nothing else changes.
export type PlanShapeModule = {
  code: PlanShapeCode;
  label: string;
  // One-line description used by badges, chips, and the agent's teaching block.
  blurb: string;
  fhirToSpec: (pd: PlanDefinition, ctx?: DecodeContext) => PlanSpec | null;
};

export const PLAN_SHAPES: Record<PlanShapeCode, PlanShapeModule> = {
  phased: {
    code: 'phased',
    label: 'Phased',
    blurb:
      'Ordered phases gated by exit criteria — variable, goal-gated timeline.',
    fhirToSpec: phasedFhirToSpec,
  },
  episodic: {
    code: 'episodic',
    label: 'Episodic',
    blurb:
      'A care baseline punctuated by defined events with defined responses.',
    fhirToSpec: episodicFhirToSpec,
  },
  continuous: {
    code: 'continuous',
    label: 'Continuous',
    blurb:
      'Open-ended management holding metrics inside control bands on a cadence.',
    fhirToSpec: continuousFhirToSpec,
  },
};

function isShapeCode(code: string | undefined): code is PlanShapeCode {
  return code !== undefined && code in PLAN_SHAPES;
}

// The shape stamped on a stored plan, falling back to inference for plans that
// predate the extension (phase-coded actions ⇒ phased).
export function planShapeOf(pd: PlanDefinition): PlanShapeCode | undefined {
  const stamped = pd.extension?.find(
    (e) => e.url === PLAN_SHAPE_EXTENSION_URL,
  )?.valueCode;
  if (isShapeCode(stamped)) {
    return stamped;
  }
  if ((pd.action ?? []).some(isPhaseAction)) {
    return 'phased';
  }
  return undefined;
}

// Deterministic compile: Plan Spec → the exact resources Save inserts — the
// PlanDefinition plus its plan-scoped ActivityDefinitions. present_plan runs
// Medplum $validate on every resource, so a clean draft is guaranteed to
// insert (same validateRepositoryResourceStrictly as create).
export function specToResources(spec: PlanSpec): CompiledPlan {
  switch (spec.shape) {
    case 'phased':
      return phasedSpecToResources(spec);
    case 'episodic':
      return episodicSpecToResources(spec);
    case 'continuous':
      return continuousSpecToResources(spec);
  }
}

// The PlanDefinition-only view of the compile, for callers that don't touch
// the ActivityDefinitions.
export function specToFhir(spec: PlanSpec): PlanDefinition {
  return specToResources(spec).planDefinition;
}

// Deterministic decode: stored plan → its Plan Spec, dispatched on the
// stamped/inferred shape. Pass the plan's resolved ActivityDefinitions in
// `ctx` to recover exercise-intrinsic fields; without them activity entries
// degrade to what the child actions carry. Null when the plan has no
// recognizable shape — callers degrade gracefully (raw/summary rendering).
export function fhirToSpec(
  pd: PlanDefinition,
  ctx?: DecodeContext,
): PlanSpec | null {
  const shape = planShapeOf(pd);
  if (!shape) {
    return null;
  }
  return PLAN_SHAPES[shape].fhirToSpec(pd, ctx);
}
